import React, { useState, useEffect } from "react";
import {
  ShoppingCart,
  Receipt,
  Camera,
  User,
  CreditCard,
  MapPin,
  Plus,
  X,
  Loader2,
  CheckCircle,
  Search,
  Pencil,
  Trash2,
  WifiOff,
} from "lucide-react";
import api from "../api/axiosInstance";

const EMPTY_FORM = {
  project_id: "",
  biller_name: "",
  amount: "",
  purchase_date: new Date().toISOString().split("T")[0],
  paid_by: "",
  payment_method: "Petty Cash",
  items_summary: "",
  bill_screenshot: null,
};

const MaterialPurchaseTracker = () => {
  const [purchases, setPurchases] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null); // null = add mode, number = edit mode
  const [deletingId, setDeletingId] = useState(null); // id being deleted (for spinner)
  const [submitting, setSubmitting] = useState(false); // form submit in-flight
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const [formData, setFormData] = useState(EMPTY_FORM);

  // ── Online / offline detection ──────────────────────────────────────────────
  useEffect(() => {
    const goOnline  = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online",  goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online",  goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [purRes, projRes] = await Promise.all([
        api.get("get_material_purchases.php"),
        api.get("get_projects.php"),
      ]);
      setPurchases(purRes.data.purchases || []);
      setProjects(projRes.data.projects || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, bill_screenshot: e.target.files[0] });
  };

  // ── Open modal for ADD ──────────────────────────────────────────────────────
  const openAddModal = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  // ── Open modal for EDIT ─────────────────────────────────────────────────────
  const openEditModal = (purchase) => {
    setEditingId(purchase.id);
    setFormData({
      project_id: purchase.project_id || "",
      biller_name: purchase.biller_name || "",
      amount: purchase.amount || "",
      purchase_date: purchase.purchase_date || EMPTY_FORM.purchase_date,
      paid_by: purchase.paid_by || "",
      payment_method: purchase.payment_method || "Petty Cash",
      items_summary: purchase.items_summary || "",
      bill_screenshot: null, // can't pre-fill file input; user re-uploads if needed
    });
    setShowModal(true);
  };

  // ── Close modal ─────────────────────────────────────────────────────────────
  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
  };

  // ── Submit (add OR edit) ────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null) data.append(key, formData[key]);
    });

    try {
      let res;
      if (editingId) {
        data.append("id", editingId);
        res = await api.post("update_material_purchase.php", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        res = await api.post("save_material_purchase.php", data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      if (res.data.status === "success") {
        alert(
          editingId
            ? "Purchase updated successfully!"
            : "Material purchase saved successfully!"
        );
        closeModal();
        loadData();
      } else if (res.data.status === "error") {
        alert(res.data.message);
      }
    } catch (err) {
      alert("Operation failed. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this purchase? This cannot be undone."))
      return;

    setDeletingId(id);
    try {
      const res = await api.post("delete_material_purchase.php", { id });
      if (res.data.status === "success") {
        setPurchases((prev) => prev.filter((p) => p.id !== id));
      } else {
        alert(res.data.message || "Delete failed.");
      }
    } catch (err) {
      alert("Delete failed. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading)
    return (
      <div className="p-20 text-center font-black animate-pulse">
        Loading Procurement Logs...
      </div>
    );

  return (
    <div className="space-y-8 p-4">
      {/* Offline banner */}
      {!isOnline && (
        <div className="flex items-center gap-3 px-6 py-3 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm font-bold">
          <WifiOff size={16} className="shrink-0" />
          You are offline — actions are disabled until your connection is restored.
        </div>
      )}
      {/* Header */}
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase">
            Site Procurement
          </h1>
          <p className="text-slate-500 font-medium italic text-sm">
            Direct Material Buys &amp; Vendor Payments
          </p>
        </div>
        <button
          onClick={openAddModal}
          disabled={submitting || !isOnline}
          title={!isOnline ? "No internet connection" : undefined}
          className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl hover:bg-slate-900 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ShoppingCart size={18} /> Add Purchase
        </button>
      </div>

      {/* Purchase List */}
      <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-slate-50/80 text-[10px] font-black text-slate-400 uppercase tracking-widest">
            <tr>
              <th className="p-8">Date / Shop</th>
              <th className="p-8">Site / Items</th>
              <th className="p-8">Payment Info</th>
              <th className="p-8 text-right">Amount</th>
              <th className="p-8 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50 font-bold">
            {purchases.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50/30 group">
                <td className="p-8">
                  <p className="text-xs text-slate-400 mb-1">
                    {p.purchase_date}
                  </p>
                  <p className="text-slate-900 uppercase italic tracking-tight">
                    {p.biller_name}
                  </p>
                </td>
                <td className="p-8">
                  <p className="text-indigo-600 text-xs uppercase mb-1">
                    {p.project_name}
                  </p>
                  <p className="text-[10px] text-slate-400 font-medium truncate w-48">
                    {p.items_summary}
                  </p>
                </td>
                <td className="p-8">
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-1 rounded text-[9px] uppercase font-black ${
                        p.payment_method === "Petty Cash"
                          ? "bg-amber-50 text-amber-600"
                          : "bg-blue-50 text-blue-600"
                      }`}
                    >
                      {p.payment_method}
                    </span>
                    <span className="text-[10px] text-slate-500 uppercase">
                      by {p.paid_by}
                    </span>
                  </div>
                </td>
                <td className="p-8 text-right text-lg font-black italic">
                  ₹{parseFloat(p.amount).toLocaleString()}
                </td>
                {/* ── Actions ── */}
                <td className="p-8">
                  <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Edit */}
                    <button
                      onClick={() => openEditModal(p)}
                      disabled={!isOnline}
                      title={!isOnline ? "No internet connection" : "Edit purchase"}
                      className="p-2 rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Pencil size={14} />
                    </button>
                    {/* Delete */}
                    <button
                      onClick={() => handleDelete(p.id)}
                      disabled={deletingId === p.id || !isOnline}
                      title={!isOnline ? "No internet connection" : "Delete purchase"}
                      className="p-2 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {deletingId === p.id ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal: Add / Edit Purchase */}
      {showModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-md">
          <form
            onSubmit={handleSubmit}
            className="bg-white w-full max-w-2xl rounded-[48px] shadow-2xl p-12 space-y-6 overflow-y-auto max-h-[90vh]"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-3xl font-black italic tracking-tighter uppercase">
                {editingId ? "Edit Purchase" : "Material Purchase"}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="p-2 bg-slate-100 rounded-full"
              >
                <X />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                  Shop/Biller Name
                </label>
                <input
                  required
                  className="w-full p-4 bg-slate-50 rounded-2xl font-bold"
                  placeholder="E.g. National Hardware"
                  value={formData.biller_name}
                  onChange={(e) =>
                    setFormData({ ...formData, biller_name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                  Select Site
                </label>
                <select
                  required
                  className="w-full p-4 bg-slate-50 rounded-2xl font-bold"
                  value={formData.project_id}
                  onChange={(e) =>
                    setFormData({ ...formData, project_id: e.target.value })
                  }
                >
                  <option value="">Choose Site...</option>
                  {projects.map((pj) => (
                    <option key={pj.id} value={pj.id}>
                      {pj.project_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                  Amount Paid
                </label>
                <input
                  required
                  type="number"
                  className="w-full p-4 bg-slate-50 rounded-2xl font-black text-xl"
                  placeholder="₹ 0.00"
                  value={formData.amount}
                  onChange={(e) =>
                    setFormData({ ...formData, amount: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                  Date
                </label>
                <input
                  type="date"
                  className="w-full p-4 bg-slate-50 rounded-2xl font-bold"
                  value={formData.purchase_date}
                  onChange={(e) =>
                    setFormData({ ...formData, purchase_date: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                  Who Paid?
                </label>
                <input
                  required
                  className="w-full p-4 bg-slate-50 rounded-2xl font-bold"
                  placeholder="Supervisor Name / Admin"
                  value={formData.paid_by}
                  onChange={(e) =>
                    setFormData({ ...formData, paid_by: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                  Method
                </label>
                <select
                  className="w-full p-4 bg-slate-50 rounded-2xl font-bold"
                  value={formData.payment_method}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      payment_method: e.target.value,
                    })
                  }
                >
                  <option value="Petty Cash">Petty Cash (From Site)</option>
                  <option value="UPI/Online">UPI/GPay (From Admin)</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cash (Admin)">Cash (Office)</option>
                </select>
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                Items Bought
              </label>
              <textarea
                className="w-full p-4 bg-slate-50 rounded-2xl font-medium text-sm h-20"
                placeholder="E.g. 5kg Fevicol, 2 Boxes of screws..."
                value={formData.items_summary}
                onChange={(e) =>
                  setFormData({ ...formData, items_summary: e.target.value })
                }
              />
            </div>

            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-400 uppercase ml-2">
                Bill Screenshot{editingId && " (leave empty to keep existing)"}
              </label>
              <div className="relative w-full p-8 border-2 border-dashed border-slate-200 rounded-3xl flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-all cursor-pointer">
                <Camera className="text-slate-400 mb-2" size={32} />
                <p className="text-[10px] font-black text-slate-400 uppercase">
                  Click to upload bill
                </p>
                <input
                  type="file"
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={handleFileChange}
                />
                {formData.bill_screenshot && (
                  <p className="mt-2 text-indigo-600 font-bold text-xs">
                    File: {formData.bill_screenshot.name}
                  </p>
                )}
              </div>
            </div>

            <button
              disabled={submitting || !isOnline}
              title={!isOnline ? "No internet connection" : undefined}
              className="w-full py-6 bg-slate-900 text-white rounded-[32px] font-black text-xl shadow-2xl hover:bg-indigo-600 transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting && <Loader2 size={22} className="animate-spin" />}
              {!isOnline
                ? <><WifiOff size={20} /> Offline</>
                : submitting
                  ? editingId ? "Saving..." : "Registering..."
                  : editingId ? "Save Changes" : "Register Purchase"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default MaterialPurchaseTracker;
